/**
 * The map-pin type catalog, as mobile reads it from the Firebase Remote Config
 * parameter `mapPinTypes2`. Web consumes the same parameter so membership,
 * order, grouping and display names cannot drift between the platforms. Icons
 * and default colours are NOT in the catalog on either platform — they stay in
 * bundled tables keyed by id (MapPin.tsx, mapPinTypes.ts).
 *
 * This file is pure: no fetching, no React. The clubhouse package fetches the
 * raw string; PinTypeCatalogProvider hands the built catalog to components.
 */

export interface PinTypeEntry {
  id: string
  /** Locale code → display name. `base` is the catalog's own fallback name. */
  name: Record<string, string>
  /** Icon id a GROUP row draws (mobile: `groupIcon ?? id`). */
  groupIcon?: string
  /** Non-empty for a group. Mobile: `isGroup => groupMembers.isNotEmpty`. */
  members: PinTypeEntry[]
}

/** The symbol a new `custom` pin starts with — mobile's `defaultCustomSymbol`. */
export const DEFAULT_CUSTOM_PIN_SYMBOL = 'A'

/**
 * Parses an already-decoded JSON value. Mirrors mobile's
 * `pin_type_catalog_parser.dart`: a non-list yields an empty catalog; an entry
 * without a string id is skipped and the rest survive; `name` defaults to an
 * empty map (non-string values dropped); `groupMembers` defaults to empty.
 */
export function parsePinTypeCatalogValue(value: unknown): PinTypeEntry[] {
  if (!Array.isArray(value)) return []
  const out: PinTypeEntry[] = []
  for (const item of value) {
    const entry = parseEntry(item)
    if (entry) out.push(entry)
  }
  return out
}

function parseEntry(item: unknown): PinTypeEntry | null {
  if (typeof item !== 'object' || item === null) return null
  const rec = item as Record<string, unknown>
  if (typeof rec.id !== 'string' || rec.id.length === 0) return null
  const name: Record<string, string> = {}
  if (typeof rec.name === 'object' && rec.name !== null) {
    for (const [k, v] of Object.entries(rec.name as Record<string, unknown>)) {
      if (typeof v === 'string') name[k] = v
    }
  }
  const entry: PinTypeEntry = { id: rec.id, name, members: parsePinTypeCatalogValue(rec.groupMembers) }
  if (typeof rec.groupIcon === 'string' && rec.groupIcon.length > 0) entry.groupIcon = rec.groupIcon
  return entry
}

/** Parses the raw Remote Config string. Malformed or empty → empty catalog. */
export function parsePinTypeCatalog(raw: string | null | undefined): PinTypeEntry[] {
  if (!raw) return []
  try {
    return parsePinTypeCatalogValue(JSON.parse(raw))
  } catch {
    return []
  }
}

/**
 * The catalog locale key for an i18next language tag. The catalog keys
 * Norwegian as `nb`; the web app speaks `no`, `nb` and `nn`, all of which are
 * Norwegian for this purpose. Regions are stripped (`sv-SE` → `sv`).
 */
export function catalogLocale(language: string | undefined): string {
  const base = (language ?? '').split(/[-_]/)[0]?.toLowerCase() ?? ''
  if (base === '') return 'base'
  if (base === 'no' || base === 'nb' || base === 'nn') return 'nb'
  return base
}

export interface PinTypeCatalog {
  /** False until a Remote Config value (possibly empty) has been parsed. */
  ready: boolean
  /** Top-level entries in catalog order — what a picker lists first. */
  entries: readonly PinTypeEntry[]
  /** Every non-group entry, depth-first in catalog order — the pickable types. */
  leaves: readonly PinTypeEntry[]
  byId(id: string): PinTypeEntry | undefined
  isKnown(id: string): boolean
  /** `name[locale] ?? name.base ?? id` — mobile's chain, no English step. */
  label(id: string): string
  /** The icon a picker row draws: the group icon for a group, else the id. */
  iconId(entry: PinTypeEntry): string
  /**
   * `isKnown`, but only once the catalog is both fetched AND non-empty;
   * `undefined` otherwise. A caller that filters a user's hidden-pin-type
   * list against `isKnown` directly would, on an empty-but-ready catalog
   * (Remote Config fetched but returned nothing, or is unreachable), find
   * every id "unknown" and clamp the whole list away — and that erasure then
   * gets written back to storage, so a transient empty catalog permanently
   * loses the user's choice. Passing `knownPredicate` instead means an empty
   * catalog is treated the same as a not-yet-fetched one: nothing is clamped.
   */
  knownPredicate: ((id: string) => boolean) | undefined
}

export function buildPinTypeCatalog(
  entries: readonly PinTypeEntry[],
  locale: string,
  ready: boolean,
): PinTypeCatalog {
  const index = new Map<string, PinTypeEntry>()
  const leaves: PinTypeEntry[] = []
  const walk = (list: readonly PinTypeEntry[]) => {
    for (const e of list) {
      if (!index.has(e.id)) index.set(e.id, e)
      if (e.members.length > 0) walk(e.members)
      else leaves.push(e)
    }
  }
  walk(entries)
  const isKnown = (id: string) => index.has(id)
  return {
    ready,
    entries,
    leaves,
    byId: (id) => index.get(id),
    isKnown,
    label: (id) => {
      const e = index.get(id)
      if (!e) return id
      return e.name[locale] ?? e.name.base ?? id
    },
    iconId: (entry) => entry.groupIcon ?? entry.id,
    knownPredicate: ready && entries.length > 0 ? isKnown : undefined,
  }
}

/** A catalog with nothing in it and `ready: false` — the pre-fetch state. */
export const EMPTY_PIN_TYPE_CATALOG: PinTypeCatalog = buildPinTypeCatalog([], 'base', false)
